import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Activity, CalendarClock, Trash2 } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  getStoreMembers,
  adminRemoveMember,
  listAppointmentServices,
  listStaffServices,
} from "@/lib/api/admin";
import type { StoreMember } from "@/lib/api/types";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Spinner";
import { Users } from "lucide-react";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { StaffActivityDialog } from "./StaffActivityDialog";
import { AddMemberDialog } from "./AddMemberDialog";
import { MemberAppointmentProfileDialog } from "./dialogs/MemberAppointmentProfileDialog";

type StoreMembersSectionProps = {
  storeId: string;
  canManageAppointments: boolean;
};

export function StoreMembersSection({ storeId, canManageAppointments }: StoreMembersSectionProps) {
  const user = useAdminAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedStaff, setSelectedStaff] = useState<{ id: string; name: string } | null>(null);
  const [addMemberRole, setAddMemberRole] = useState<"owner" | "manager" | "staff" | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<StoreMember | null>(null);
  const [editingProfileMember, setEditingProfileMember] = useState<StoreMember | null>(null);
  const [editingProfileServiceIds, setEditingProfileServiceIds] = useState<string[]>([]);
  const [loadingProfileFor, setLoadingProfileFor] = useState<string | null>(null);

  const { data: members, isLoading } = useQuery({
    queryKey: ["store-members", storeId],
    queryFn: async () => getStoreMembers(user!, storeId),
    enabled: !!user,
  });

  const { data: services } = useQuery({
    queryKey: ["appointment-services", storeId],
    queryFn: async () => listAppointmentServices(user!, storeId),
    enabled: !!user && canManageAppointments,
  });

  async function openAppointmentProfile(member: StoreMember) {
    setLoadingProfileFor(member.user_uid);
    try {
      const offered = await listStaffServices(user!, storeId, member.user_uid);
      setEditingProfileServiceIds(offered.map((s) => s.id));
      setEditingProfileMember(member);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoadingProfileFor(null);
    }
  }

  const removeMutation = useMutation({
    mutationFn: (memberId: string) => adminRemoveMember(user!, storeId, memberId),
    onSuccess: () => {
      toast.success("Member removed");
      void queryClient.invalidateQueries({ queryKey: ["store-members", storeId] });
      setMemberToRemove(null);
    },
    onError: (err: Error) => {
      toast.error(err.message);
      setMemberToRemove(null);
    },
  });

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="size-4" /> Members
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-4">
              <Spinner className="size-4 border-primary" />
            </div>
          ) : members?.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members.</p>
          ) : (
            <ul className="divide-y">
              {members?.map((m) => (
                <li
                  key={m.id}
                  className="flex items-center justify-between gap-2 py-2 text-sm rounded-md px-1 -mx-1 transition-colors cursor-pointer hover:bg-muted/50"
                  onClick={() => {
                    if (m.role === "staff" && m.user_uid.startsWith("staff:")) {
                      // Legacy staff (internal login) — activity dialog only
                      const rawId = m.user_uid.replace(/^staff:/, "");
                      setSelectedStaff({ id: rawId, name: `${m.first_name} ${m.last_name}` });
                    } else {
                      // Owners, managers, and Firebase-auth staff → user profile
                      void navigate(`/users/${m.user_uid}`);
                    }
                  }}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">
                      {m.first_name} {m.last_name}
                    </p>
                    <p className="text-xs font-mono text-muted-foreground truncate">{m.user_uid}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground capitalize rounded-full bg-muted px-2 py-0.5">
                      {m.role}
                    </span>
                    {!m.user_uid.startsWith("staff:") && (
                      <button
                        type="button"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedStaff({
                            id: m.user_uid,
                            name: `${m.first_name} ${m.last_name}`,
                          });
                        }}
                        aria-label={`View ${m.first_name} ${m.last_name}'s scanner activity`}
                      >
                        <Activity className="size-4" />
                      </button>
                    )}
                    {canManageAppointments && !m.user_uid.startsWith("staff:") && (
                      <button
                        type="button"
                        className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
                        disabled={loadingProfileFor === m.user_uid}
                        onClick={(e) => {
                          e.stopPropagation();
                          void openAppointmentProfile(m);
                        }}
                        aria-label={`Edit ${m.first_name} ${m.last_name}'s appointment profile`}
                      >
                        {loadingProfileFor === m.user_uid ? (
                          <Spinner className="size-4 border-muted-foreground" />
                        ) : (
                          <CalendarClock className="size-4" />
                        )}
                      </button>
                    )}
                    <button
                      type="button"
                      className="p-1 rounded text-muted-foreground hover:text-destructive hover:bg-muted/50 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMemberToRemove(m);
                      }}
                      aria-label={`Remove ${m.first_name} ${m.last_name}`}
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
        <CardFooter className="flex gap-2 pt-0 flex-wrap">
          <Button variant="secondary" size="sm" onClick={() => setAddMemberRole("owner")}>
            Add Owner
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAddMemberRole("manager")}>
            Add Manager
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setAddMemberRole("staff")}>
            Add Staff
          </Button>
        </CardFooter>
      </Card>

      <StaffActivityDialog staff={selectedStaff} onClose={() => setSelectedStaff(null)} />

      {editingProfileMember && (
        <MemberAppointmentProfileDialog
          open={!!editingProfileMember}
          onClose={() => setEditingProfileMember(null)}
          storeId={storeId}
          member={editingProfileMember}
          services={services ?? []}
          initialServiceIds={editingProfileServiceIds}
        />
      )}

      <AddMemberDialog
        open={!!addMemberRole}
        onOpenChange={(open) => {
          if (!open) setAddMemberRole(null);
        }}
        storeId={storeId}
        role={addMemberRole ?? "owner"}
      />

      <ConfirmDialog
        open={!!memberToRemove}
        onOpenChange={(open) => {
          if (!open) setMemberToRemove(null);
        }}
        title="Remove member"
        description={
          memberToRemove
            ? `Remove ${memberToRemove.first_name} ${memberToRemove.last_name} as ${memberToRemove.role}?`
            : ""
        }
        confirmLabel="Remove"
        destructive
        isPending={removeMutation.isPending}
        onConfirm={() => {
          if (memberToRemove) removeMutation.mutate(memberToRemove.id);
        }}
      />
    </>
  );
}
